import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { bibleLink } from '../bible/BiblePage';
import { useMe } from '../session/SessionProvider';

type Kind = 'ayuno' | 'oracion' | 'lectura' | 'otra';
const KIND: Record<Kind, string> = { ayuno: 'Ayuno', oracion: 'Oración', lectura: 'Lectura', otra: 'Campaña' };
type Day = { day: number; passage: string | null; reflection: string | null; prayer: string | null; activity: string | null };
type Detail = { id: string; kind: Kind; title: string; purpose: string | null; guide: string | null; startsOn: string; totalDays: number; participants: number; joined: boolean; today: number; canManage: boolean; days: Day[]; completed: number[] };
const dateOf = (start: string, day: number) => new Date(Date.parse(`${start}T12:00:00`) + (day - 1) * 86400_000).toLocaleDateString('es', { weekday: 'short', day: 'numeric', month: 'short' });

type CampaignBody = { kind: string | null; title: string | null; purpose: string | null; guide: string | null; startsOn: string | null; days: Omit<Day, 'day'>[] };

/** Formulario de una campaña: crear o corregir (nombre, fecha, propósito, guía y lo de cada día). */
function CampaignForm({ initial, title, submitLabel, busy, error, onSubmit, onCancel, extra }: { initial?: Detail; title: string; submitLabel: string; busy: boolean; error: string | null; onSubmit: (body: CampaignBody) => void; onCancel: () => void; extra?: ReactNode }) {
  const [days, setDays] = useState<Day[]>(initial?.days.length ? initial.days : [{ day: 1, passage: null, reflection: null, prayer: null, activity: null }]);
  const setDay = (i: number, k: keyof Omit<Day, 'day'>, v: string) => setDays(days.map((d, j) => (j === i ? { ...d, [k]: v || null } : d)));
  return (
    <Card title={title}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          const t = (k: string) => String(f.get(k) ?? '').trim() || null;
          onSubmit({ kind: t('kind'), title: t('title'), purpose: t('purpose'), guide: t('guide'), startsOn: t('startsOn'), days: days.map(({ day: _d, ...rest }) => rest) });
        }}
      >
        {error ? <Alert>{error}</Alert> : null}
        <div className="form__row">
          <div className="field">
            <label htmlFor="cp-kind">Tipo</label>
            <select id="cp-kind" name="kind" defaultValue={initial?.kind ?? 'ayuno'}>{Object.entries(KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          </div>
          <Field label="Empieza" name="startsOn" type="date" required defaultValue={initial?.startsOn ?? ''} />
        </div>
        <Field label="Nombre" name="title" required minLength={3} maxLength={120} placeholder="21 días de oración" defaultValue={initial?.title ?? ''} />
        <div className="field"><label htmlFor="cp-purpose">Propósito</label><textarea id="cp-purpose" name="purpose" rows={2} maxLength={2000} defaultValue={initial?.purpose ?? ''} /></div>
        <div className="field"><label htmlFor="cp-guide">Guía (tipo de ayuno, recomendaciones, materiales)</label><textarea id="cp-guide" name="guide" rows={3} maxLength={3000} defaultValue={initial?.guide ?? ''} /></div>
        <h3 className="small">Días ({days.length})</h3>
        {days.map((d, i) => (
          <fieldset key={i} className="campaign-day">
            <legend>Día {i + 1}</legend>
            <div className="form__row">
              <Field label="Lectura" value={d.passage ?? ''} onChange={(e) => setDay(i, 'passage', e.target.value)} placeholder="Mateo 6:16-18" />
              <Field label="Actividad" value={d.activity ?? ''} onChange={(e) => setDay(i, 'activity', e.target.value)} />
            </div>
            <Field label="Reflexión" value={d.reflection ?? ''} onChange={(e) => setDay(i, 'reflection', e.target.value)} />
            <Field label="Oración" value={d.prayer ?? ''} onChange={(e) => setDay(i, 'prayer', e.target.value)} />
          </fieldset>
        ))}
        <div className="actions actions--wrap">
          <Button variant="secondary" disabled={days.length >= 90} onClick={() => setDays([...days, { day: days.length + 1, passage: null, reflection: null, prayer: null, activity: null }])}>Agregar día</Button>
          {days.length > 1 ? <Button variant="ghost" onClick={() => setDays(days.slice(0, -1))}>Quitar el último día</Button> : null}
        </div>
        <div className="actions"><Button type="submit" busy={busy}>{submitLabel}</Button><Button variant="ghost" onClick={onCancel}>Cancelar</Button>{extra}</div>
      </form>
    </Card>
  );
}

/** Ayunos y campañas espirituales: lista y crear. */
export function CampaignsPage() {
  const me = useMe();
  const base = `/devotional/churches/${me.activeChurchId}/campaigns`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<{ canManage: boolean; items: { id: string; kind: Kind; title: string; startsOn: string; days: number; participants: number; joined: boolean; today: number }[] }>(base, signal) : Promise.resolve(null)), [base]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!me.activeChurchId) return <EmptyState title="Elige primero una iglesia." />;

  return (
    <div className="page">
      <header className="page__header">
        <h1>Ayunos y campañas</h1>
        <p className="muted">Días de ayuno, oración o lectura para toda la iglesia. Tu avance solo lo ves tú.</p>
      </header>
      {data.status === 'ready' && data.data?.canManage && !open ? <div><Button onClick={() => setOpen(true)}>Nueva campaña</Button></div> : null}
      {open ? (
        <CampaignForm
          title="Nueva campaña"
          submitLabel="Crear y avisar a la iglesia"
          busy={busy}
          error={error}
          onCancel={() => setOpen(false)}
          onSubmit={(body) => {
            setBusy(true);
            setError(null);
            api
              .post<{ id: string }>(base, { ...body, notify: true })
              .then((r) => navigate(`/app/campanas/${r.id}`))
              .catch((err) => { setError(errorMessage(err)); setBusy(false); });
          }}
        />
      ) : null}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && data.data ? (
        data.data.items.length === 0 ? <Card><EmptyState title="No hay campañas activas." /></Card> : (
          <ul className="list card">
            {data.data.items.map((c) => (
              <li key={c.id} className="list__item">
                <Link className="list__link" to={`/app/campanas/${c.id}`}>
                  <strong>{c.title}</strong>
                  <span className="small muted">
                    {KIND[c.kind]} · {c.days} días · {c.today < 1 ? `empieza el ${dateOf(c.startsOn, 1)}` : c.today > c.days ? 'terminó' : `día ${c.today}`} · {c.participants} {c.participants === 1 ? 'participa' : 'participan'}{c.joined ? ' · estás dentro' : ''}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

export function CampaignPage() {
  const { campaignId = '' } = useParams();
  const me = useMe();
  const url = `/devotional/churches/${me.activeChurchId}/campaigns/${campaignId}`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<Detail>(url, signal) : Promise.resolve(null)), [url]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} /></div>;
  const c = data.data;
  if (!c) return null;
  const act = (p: Promise<unknown>) => p.then(() => data.reload()).catch((e) => setError(errorMessage(e)));
  return (
    <div className="page page--narrow">
      <p className="small"><Link to="/app/campanas">← Ayunos y campañas</Link></p>
      <header className="page__header">
        <h1>{c.title}</h1>
        <p className="muted">{KIND[c.kind]} · {c.totalDays} días desde el {dateOf(c.startsOn, 1)} · {c.participants} {c.participants === 1 ? 'participa' : 'participan'}</p>
      </header>
      {c.canManage && !editing ? (
        <div className="actions">
          <Button variant="secondary" onClick={() => setEditing(true)}>Editar</Button>
          <Button
            variant="ghost"
            onClick={() => {
              if (window.confirm('¿Borrar esta campaña? Se pierde el avance de quienes participan.')) {
                api.delete(url).then(() => navigate('/app/campanas')).catch((e) => setError(errorMessage(e)));
              }
            }}
          >
            Borrar
          </Button>
        </div>
      ) : null}
      {editing ? (
        <CampaignForm
          initial={c}
          title="Editar campaña"
          submitLabel="Guardar"
          busy={busy}
          error={error}
          onCancel={() => setEditing(false)}
          onSubmit={(body) => {
            setBusy(true);
            setError(null);
            api
              .put(url, body)
              .then(() => { setEditing(false); data.reload(); })
              .catch((e) => setError(errorMessage(e)))
              .finally(() => setBusy(false));
          }}
        />
      ) : null}
      {error && !editing ? <Alert>{error}</Alert> : null}
      {c.purpose ? <Card title="Propósito"><p className="pre-line">{c.purpose}</p></Card> : null}
      {c.guide ? <Card title="Guía"><p className="pre-line">{c.guide}</p></Card> : null}
      <div className="actions">
        {c.joined ? (
          <Button variant="ghost" onClick={() => void act(api.put(`${url}/join`, { on: false }))}>Salir de la campaña</Button>
        ) : (
          <Button onClick={() => void act(api.put(`${url}/join`, { on: true }))}>Unirme</Button>
        )}
      </div>
      {c.joined ? <progress className="usage" max={c.totalDays} value={c.completed.length} aria-label="Mi avance" /> : null}
      {c.days.map((d) => (
        <Card key={d.day} title={`Día ${d.day} · ${dateOf(c.startsOn, d.day)}${d.day === c.today ? ' · hoy' : ''}`}>
          {d.passage ? <p><strong>Lectura:</strong> <Link to={bibleLink(d.passage)}>{d.passage}</Link></p> : null}
          {d.reflection ? <p className="pre-line">{d.reflection}</p> : null}
          {d.prayer ? <p className="pre-line"><strong>Oración:</strong> {d.prayer}</p> : null}
          {d.activity ? <p><strong>Actividad:</strong> {d.activity}</p> : null}
          {c.joined ? (
            <label className="check">
              <input type="checkbox" checked={c.completed.includes(d.day)} onChange={(e) => void act(api.put(`${url}/days/${d.day}`, { done: e.target.checked }))} /> Lo cumplí
            </label>
          ) : null}
        </Card>
      ))}
    </div>
  );
}
